import React, { useEffect, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import {
    ArrowLeft, ArrowRight, Play, Search as SearchIcon, X,
    CheckCircle2, LayoutDashboard, CalendarDays, Rocket,
    Inbox, ScanLine, BadgeCheck, GitMerge, Brain, GitBranch,
    BarChart2, Clock, Eye, AlertTriangle, Zap, TrendingUp,
    Shield, PieChart, Users, Package, ChevronRight,
} from "lucide-react";
import { useSEO } from "@/lib/seo";
import { api } from "@/lib/api";
import { absUrl } from "@/components/admin/UploadWidget";

// ── Icon maps ─────────────────────────────────────────────────────────────────
const AGENT_ICONS = {
    "Invoice Collection Agent": Inbox,
    "OCR Extraction Agent": ScanLine,
    "Invoice Validation Agent": BadgeCheck,
    "3-Way Matching Agent": GitMerge,
    "Context Resolution Agent": Brain,
    "Decision Agent": GitBranch,
    "Analytics & Insights Agent": BarChart2,
};
const PROBLEM_ICON_COMPONENTS = [Clock, Eye, AlertTriangle, Shield];
const OUTCOME_ICONS = [Zap, TrendingUp, Shield, PieChart];

// ── Section: Outcome Strip ────────────────────────────────────────────────────
function OutcomeStrip({ items }) {
    return (
        <section data-testid="section-outcome-strip" className="border-b border-border bg-white">
            <div className="container-app grid grid-cols-2 lg:grid-cols-4 divide-x divide-y lg:divide-y-0 divide-border">
                {items.map((o, idx) => (
                    <div key={idx} className="px-6 py-6 lg:py-7">
                        <div className="text-[10px] font-bold uppercase tracking-widest text-primary">{o.label}</div>
                        <div className="mt-1.5 font-heading font-bold text-foreground text-sm leading-snug">{o.title}</div>
                        <div className="mt-1 text-xs text-muted-foreground leading-relaxed">{o.description}</div>
                    </div>
                ))}
            </div>
        </section>
    );
}

// ── Section: Product Overview (What is X?) ────────────────────────────────────
function ProductOverview({ item }) {
    const shortName = item.title.replace(/^RKS (Intelligent |Systems )?/i, "");
    // Derive lifecycle steps from workflow_steps if present, or fall back to tech stack
    const lifecycleSteps = item.workflow_steps?.map(s => s.title) || [];
    const hasLifecycle = lifecycleSteps.length > 0;
    return (
        <section className="py-16 lg:py-20 border-b border-border" data-testid="section-product-overview">
            <div className="container-app">
                <div className="grid lg:grid-cols-2 gap-12 items-center">
                    <div>
                        <span className="overline text-primary">About</span>
                        <h2 className="mt-3 font-heading text-2xl sm:text-3xl font-bold">What is {shortName}?</h2>
                        <p className="mt-4 text-foreground/80 leading-relaxed">{item.description}</p>
                    </div>
                    <div className="rounded-xl border border-border bg-secondary/20 p-6">
                        {hasLifecycle ? (
                            <>
                                <div className="text-[10px] font-bold uppercase tracking-widest text-primary mb-4">Process Flow</div>
                                <div className="flex flex-wrap gap-2 items-center">
                                    {lifecycleSteps.map((step, i, arr) => (
                                        <React.Fragment key={step}>
                                            <span className="rounded-md border border-primary/30 bg-primary/5 px-3 py-1.5 text-sm font-semibold text-primary">{step}</span>
                                            {i < arr.length - 1 && <ArrowRight className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />}
                                        </React.Fragment>
                                    ))}
                                </div>
                            </>
                        ) : (
                            <div className="text-[10px] font-bold uppercase tracking-widest text-primary mb-4">Technology Stack</div>
                        )}
                        <div className="mt-6 flex flex-wrap gap-2">
                            {item.tech_stack?.map(t => (
                                <span key={t} className="text-[10px] font-semibold uppercase tracking-wider bg-secondary px-2 py-1 rounded text-secondary-foreground">{t}</span>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

// ── Section: How It Works (Workflow) ──────────────────────────────────────────
function WorkflowSection({ steps }) {
    return (
        <section className="py-16 lg:py-24 bg-[hsl(222_47%_7%)] text-white" data-testid="section-workflow">
            <div className="container-app">
                <span className="overline text-primary">How It Works</span>
                <h2 className="mt-3 font-heading text-2xl sm:text-3xl font-bold">Step-by-step workflow</h2>

                {/* Desktop horizontal */}
                <div className="hidden lg:flex mt-14 items-start">
                    {steps.map((step, idx) => (
                        <React.Fragment key={idx}>
                            <div className="flex flex-col items-center text-center px-1" style={{ width: `${100 / steps.length}%` }}>
                                <div className="h-11 w-11 rounded-full border-2 border-primary bg-primary/20 flex items-center justify-center font-heading font-extrabold text-primary text-sm relative z-10 flex-shrink-0">
                                    {step.number}
                                </div>
                                <h3 className="mt-3 font-heading font-bold text-sm text-white">{step.title}</h3>
                                <p className="mt-1.5 text-[11px] text-white/50 leading-relaxed">{step.description}</p>
                            </div>
                            {idx < steps.length - 1 && (
                                <div className="flex items-start pt-[22px] flex-1 min-w-0">
                                    <div className="h-[2px] w-full bg-primary/25" />
                                </div>
                            )}
                        </React.Fragment>
                    ))}
                </div>

                {/* Mobile vertical */}
                <div className="lg:hidden mt-10">
                    {steps.map((step, idx) => (
                        <div key={idx} className="flex gap-5">
                            <div className="flex flex-col items-center">
                                <div className="h-9 w-9 rounded-full border-2 border-primary bg-primary/20 flex items-center justify-center font-heading font-extrabold text-primary text-xs flex-shrink-0">
                                    {step.number}
                                </div>
                                {idx < steps.length - 1 && <div className="flex-1 w-[2px] bg-primary/20 my-1 min-h-[24px]" />}
                            </div>
                            <div className="pb-6">
                                <h3 className="font-heading font-bold text-white">{step.title}</h3>
                                <p className="mt-1 text-sm text-white/55 leading-relaxed">{step.description}</p>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

// ── Section: Agent Architecture ───────────────────────────────────────────────
function AgentArchitecture({ agents }) {
    return (
        <section className="py-16 lg:py-20 bg-secondary/20" data-testid="section-agents">
            <div className="container-app">
                <div className="max-w-2xl mb-10">
                    <span className="overline text-primary">Agentic AI</span>
                    <h2 className="mt-3 font-heading text-2xl sm:text-3xl font-bold">{agents.length} specialized agent{agents.length !== 1 ? "s" : ""}, one orchestrated system</h2>
                    <p className="mt-3 text-muted-foreground text-sm leading-relaxed">Each agent has a defined responsibility. Together they handle the full lifecycle — exceptions and high-risk decisions route to human review.</p>
                </div>
                <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/5 px-4 py-1.5 text-xs font-semibold text-primary">
                    <Brain className="h-3.5 w-3.5" />
                    AI Orchestration Layer
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    {agents.map((agent, idx) => {
                        const Icon = AGENT_ICONS[agent.name] || Package;
                        return (
                            <div key={idx} data-testid={`agent-card-${idx}`} className="rounded-xl border border-border bg-white p-5 hover:border-primary/40 hover:shadow-md transition-all">
                                <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center mb-3">
                                    <Icon className="h-5 w-5 text-primary" />
                                </div>
                                <h3 className="font-heading font-bold text-sm text-foreground leading-snug">{agent.name}</h3>
                                <p className="mt-1.5 text-xs text-muted-foreground leading-relaxed">{agent.responsibility}</p>
                                {agent.tags?.length > 0 && (
                                    <div className="mt-3 flex flex-wrap gap-1">
                                        {agent.tags.map(t => (
                                            <span key={t} className="text-[10px] font-semibold bg-secondary text-secondary-foreground px-1.5 py-0.5 rounded">{t}</span>
                                        ))}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                    {/* Human Review card */}
                    <div className="rounded-xl border-2 border-dashed border-primary/30 bg-primary/5 p-5 flex flex-col items-center justify-center text-center">
                        <div className="h-10 w-10 rounded-lg bg-primary/20 flex items-center justify-center mb-3">
                            <Users className="h-5 w-5 text-primary" />
                        </div>
                        <h3 className="font-heading font-bold text-sm text-primary">Human Review</h3>
                        <p className="mt-1.5 text-xs text-muted-foreground leading-relaxed">Exceptions and high-risk decisions route to human reviewers with full context.</p>
                    </div>
                </div>
            </div>
        </section>
    );
}

// ── Section: Business Problems ────────────────────────────────────────────────
function BusinessProblemsSection({ problems }) {
    return (
        <section className="py-16 lg:py-20 border-b border-border" data-testid="section-business-problems">
            <div className="container-app">
                <div className="max-w-2xl mb-10">
                    <span className="overline text-primary">The Challenge</span>
                    <h2 className="mt-3 font-heading text-2xl sm:text-3xl font-bold">Why manual AP breaks down</h2>
                    <p className="mt-3 text-muted-foreground text-sm leading-relaxed">Understanding these challenges drives the architecture and automation decisions built into this solution.</p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    {problems.map((p, idx) => {
                        const Icon = PROBLEM_ICON_COMPONENTS[idx % PROBLEM_ICON_COMPONENTS.length];
                        return (
                            <div key={idx} data-testid={`problem-card-${idx}`} className="rounded-xl border border-border bg-white p-6 hover:border-primary/20 transition-colors">
                                <div className="h-10 w-10 rounded-lg bg-red-50 flex items-center justify-center mb-4">
                                    <Icon className="h-5 w-5 text-red-500" />
                                </div>
                                <h3 className="font-heading font-bold text-foreground">{p.title}</h3>
                                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{p.description}</p>
                            </div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}

// ── Section: Human-in-the-Loop ────────────────────────────────────────────────
function HumanInTheLoop({ productName }) {
    const name = productName || "this system";
    return (
        <section className="py-16 lg:py-20 bg-[hsl(222_47%_7%)] text-white" data-testid="section-hitl">
            <div className="container-app">
                <div className="max-w-2xl mb-12">
                    <span className="overline text-primary">Control</span>
                    <h2 className="mt-3 font-heading text-2xl sm:text-3xl font-bold">AI does the work. Humans stay in control.</h2>
                    <p className="mt-3 text-white/65 text-sm leading-relaxed">{name} is not an unsupervised black box. Exceptions, ambiguity, and high-risk decisions route to a human reviewer with full context and a complete audit trail.</p>
                </div>

                {/* Decision flow */}
                <div className="flex flex-col lg:flex-row items-center lg:items-stretch gap-3 lg:gap-0 flex-wrap lg:flex-nowrap">
                    {/* AI Agents */}
                    <div className="rounded-xl border border-primary/40 bg-primary/10 p-5 text-center w-44 flex-shrink-0">
                        <Brain className="h-7 w-7 text-primary mx-auto" />
                        <div className="mt-2 font-heading font-bold text-sm text-white">AI Agents</div>
                        <div className="mt-0.5 text-[10px] text-white/45">Process invoice</div>
                    </div>
                    <div className="flex lg:flex-row flex-col items-center justify-center gap-0 px-2">
                        <div className="h-8 w-[2px] lg:h-[2px] lg:w-8 bg-primary/30" />
                        <ChevronRight className="h-4 w-4 text-primary/40 hidden lg:block" />
                        <ChevronRight className="h-4 w-4 text-primary/40 rotate-90 lg:hidden" />
                    </div>
                    {/* Rules check */}
                    <div className="rounded-xl border border-primary/40 bg-primary/10 p-5 text-center w-44 flex-shrink-0">
                        <Shield className="h-7 w-7 text-primary mx-auto" />
                        <div className="mt-2 font-heading font-bold text-sm text-white">Rules & Confidence</div>
                        <div className="mt-0.5 text-[10px] text-white/45">Policy evaluation</div>
                    </div>
                    <div className="flex lg:flex-row flex-col items-center justify-center gap-0 px-2">
                        <div className="h-8 w-[2px] lg:h-[2px] lg:w-8 bg-primary/30" />
                        <ChevronRight className="h-4 w-4 text-primary/40 hidden lg:block" />
                        <ChevronRight className="h-4 w-4 text-primary/40 rotate-90 lg:hidden" />
                    </div>
                    {/* Fork */}
                    <div className="flex flex-col gap-3">
                        <div className="rounded-xl border border-green-500/30 bg-green-500/10 p-4 text-center w-44 flex-shrink-0">
                            <CheckCircle2 className="h-6 w-6 text-green-400 mx-auto" />
                            <div className="mt-1.5 font-semibold text-xs text-white">Auto-processed</div>
                            <div className="text-[10px] text-white/40 mt-0.5">High confidence</div>
                        </div>
                        <div className="rounded-xl border border-orange-500/30 bg-orange-500/10 p-4 text-center w-44 flex-shrink-0">
                            <Users className="h-6 w-6 text-orange-400 mx-auto" />
                            <div className="mt-1.5 font-semibold text-xs text-white">Human Review</div>
                            <div className="text-[10px] text-white/40 mt-0.5">Exception / edge case</div>
                        </div>
                    </div>
                    <div className="flex lg:flex-row flex-col items-center justify-center gap-0 px-2">
                        <div className="h-8 w-[2px] lg:h-[2px] lg:w-8 bg-primary/30" />
                        <ChevronRight className="h-4 w-4 text-primary/40 hidden lg:block" />
                        <ChevronRight className="h-4 w-4 text-primary/40 rotate-90 lg:hidden" />
                    </div>
                    {/* Audit trail */}
                    <div className="rounded-xl border border-white/15 bg-white/[0.04] p-5 text-center w-44 flex-shrink-0">
                        <BarChart2 className="h-7 w-7 text-primary mx-auto" />
                        <div className="mt-2 font-heading font-bold text-sm text-white">Audit Trail</div>
                        <div className="mt-0.5 text-[10px] text-white/45">Full decision trace</div>
                    </div>
                </div>

                {/* Human actions */}
                <div className="mt-10 flex flex-wrap gap-3 items-center">
                    <span className="text-xs font-semibold text-white/40">Human actions:</span>
                    {["Approve", "Reject", "Override", "Correct & Reprocess", "Escalate"].map(action => (
                        <span key={action} className="rounded-full border border-white/20 bg-white/[0.05] px-4 py-1.5 text-xs font-semibold text-white/75">{action}</span>
                    ))}
                </div>
            </div>
        </section>
    );
}

// ── Section: Business Outcomes ────────────────────────────────────────────────
function BusinessOutcomes({ outcomes }) {
    return (
        <section className="py-16 lg:py-20 bg-secondary/20" data-testid="section-outcomes">
            <div className="container-app">
                <div className="max-w-xl mb-10">
                    <span className="overline text-primary">Outcomes</span>
                    <h2 className="mt-3 font-heading text-2xl sm:text-3xl font-bold">What you gain</h2>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                    {outcomes.map((o, idx) => {
                        const Icon = OUTCOME_ICONS[idx % OUTCOME_ICONS.length];
                        return (
                            <div key={idx} data-testid={`outcome-card-${idx}`} className="rounded-xl border border-border bg-white p-6">
                                <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                                    <Icon className="h-5 w-5 text-primary" />
                                </div>
                                <h3 className="font-heading font-bold text-foreground text-sm">{o.label}</h3>
                                <p className="mt-2 text-xs text-muted-foreground leading-relaxed">{o.description}</p>
                            </div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}

// ── Section: Product CTA ──────────────────────────────────────────────────────
function ProductCTA({ item }) {
    const name = item.title.replace(/^RKS (Intelligent |Systems )?/i, "");
    return (
        <section className="bg-[hsl(222_47%_7%)] text-white noise py-20 lg:py-28" data-testid="section-product-cta">
            <div className="container-app">
                <div className="text-center mb-12">
                    <span className="overline text-primary">Get Started</span>
                    <h2 className="mt-3 font-heading text-3xl sm:text-4xl font-extrabold tracking-tight">Two paths forward</h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
                    {/* Learn */}
                    <div className="rounded-2xl border border-primary/30 bg-white/[0.04] p-8 flex flex-col gap-5">
                        <div>
                            <div className="text-[10px] font-bold uppercase tracking-widest text-primary mb-2">Learn</div>
                            <div className="h-12 w-12 rounded-xl bg-primary/20 flex items-center justify-center">
                                <CalendarDays className="h-6 w-6 text-primary" />
                            </div>
                        </div>
                        <div>
                            <h3 className="font-heading text-xl font-bold text-white">See {name} in action</h3>
                            <p className="mt-2 text-white/65 text-sm leading-relaxed">Schedule a Demo — our team walks you through a live platform session tailored to your AP workflow.</p>
                        </div>
                        <ul className="space-y-2">
                            {["30-minute focused session", "Live platform walkthrough", "Q&A with our product team"].map(pt => (
                                <li key={pt} className="flex items-center gap-2 text-sm text-white/70">
                                    <CheckCircle2 className="h-3.5 w-3.5 text-primary flex-shrink-0" />{pt}
                                </li>
                            ))}
                        </ul>
                        <Link to="/schedule-meeting" data-testid="product-cta-schedule-demo" className="mt-auto inline-flex items-center justify-center gap-2 rounded-xl bg-primary px-6 py-3 text-sm font-semibold text-white hover:bg-primary/90 transition-colors">
                            <CalendarDays className="h-4 w-4" />Schedule a Demo
                        </Link>
                    </div>
                    {/* Prove */}
                    <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-8 flex flex-col gap-5 relative overflow-hidden">
                        <div className="absolute top-4 right-4">
                            <span className="rounded-full bg-primary/20 border border-primary/40 px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-primary">Free Offer</span>
                        </div>
                        <div>
                            <div className="text-[10px] font-bold uppercase tracking-widest text-white/40 mb-2">Prove</div>
                            <div className="h-12 w-12 rounded-xl bg-white/10 flex items-center justify-center">
                                <Rocket className="h-6 w-6 text-white" />
                            </div>
                        </div>
                        <div>
                            <h3 className="font-heading text-xl font-bold text-white">Prove it in your environment</h3>
                            <p className="mt-2 text-white/65 text-sm leading-relaxed">Start a Free Pilot — we configure {name} in your environment so you can validate ROI before you commit.</p>
                        </div>
                        <ul className="space-y-2">
                            {["Fully configured in your environment", "2-week hands-on pilot", "Measured outcome report"].map(pt => (
                                <li key={pt} className="flex items-center gap-2 text-sm text-white/70">
                                    <CheckCircle2 className="h-3.5 w-3.5 text-white/35 flex-shrink-0" />{pt}
                                </li>
                            ))}
                        </ul>
                        <Link to="/contact" data-testid="product-cta-free-pilot" className="mt-auto inline-flex items-center justify-center gap-2 rounded-xl border border-white/20 bg-white/10 px-6 py-3 text-sm font-semibold text-white hover:bg-white/20 transition-colors">
                            <Rocket className="h-4 w-4" />Start a Free Pilot
                        </Link>
                    </div>
                </div>
            </div>
        </section>
    );
}

// ── Products Listing Page ─────────────────────────────────────────────────────
export function Products() {
    useSEO({
        title: "Products | RKS Systems",
        description: "Production-grade products RKS Systems has built for real business problems — with the architecture, features, tech stack, and outcomes.",
    });
    const [items, setItems] = useState([]);
    const [q, setQ] = useState("");
    const [category, setCategory] = useState("all");
    const [loading, setLoading] = useState(true);

    const loadItems = async () => {
        setLoading(true);
        try {
            const params = {};
            if (q) params.q = q;
            if (category && category !== "all") params.category = category;
            const { data } = await api.get("/products", { params });
            setItems(data.items || []);
        } finally { setLoading(false); }
    };

    useEffect(() => { loadItems(); }, [category]);

    const categories = ["all", ...Array.from(new Set(items.map(i => i.category))).filter(Boolean)];

    return (
        <div data-testid="page-products">
            <section className="bg-[hsl(222_47%_7%)] text-white noise">
                <div className="container-app py-24 lg:py-32">
                    <div className="max-w-3xl">
                        <span className="overline text-primary">Products</span>
                        <h1 className="mt-4 font-heading text-5xl sm:text-6xl font-extrabold tracking-tight leading-[1.05]">Production-grade products.</h1>
                        <p className="mt-6 text-lg text-white/70 leading-relaxed">Every product is a real solution to a real business problem — with the architecture, features, constraints, and outcome disclosed.</p>
                    </div>
                </div>
            </section>
            <section className="py-16 lg:py-20">
                <div className="container-app">
                    <div className="flex flex-col md:flex-row md:items-center gap-4 mb-10">
                        <form onSubmit={e => { e.preventDefault(); loadItems(); }} className="flex-1 flex items-center gap-3 rounded-md border border-border bg-white px-4 py-3">
                            <SearchIcon className="h-4 w-4 text-muted-foreground" strokeWidth={1.75} />
                            <input data-testid="products-search-input" value={q} onChange={e => setQ(e.target.value)} placeholder="Search by title, tech stack..." className="flex-1 bg-transparent outline-none text-sm placeholder:text-muted-foreground" />
                            {q && <button type="button" onClick={() => { setQ(""); setTimeout(loadItems, 0); }}><X className="h-4 w-4 text-muted-foreground" /></button>}
                            <button type="submit" data-testid="products-search-submit" className="text-sm font-semibold text-primary hover:underline">Search</button>
                        </form>
                        <div className="flex flex-wrap gap-2">
                            {categories.map(c => (
                                <button key={c} onClick={() => setCategory(c)} data-testid={`products-filter-${c}`} className={`text-xs font-semibold uppercase tracking-wider px-3.5 py-2 rounded-full border transition-all ${category === c ? "bg-primary text-primary-foreground border-primary" : "bg-white text-foreground/70 border-border hover:border-primary/40"}`}>
                                    {c === "all" ? "All" : c}
                                </button>
                            ))}
                        </div>
                    </div>
                    {loading ? (
                        <div className="text-center text-muted-foreground py-20">Loading products...</div>
                    ) : items.length === 0 ? (
                        <div className="text-center text-muted-foreground py-20" data-testid="products-empty">No products found.</div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {items.map(p => (
                                <Link key={p.id} to={`/products/${p.id}`} data-testid={`product-card-${p.id}`} className="group relative overflow-hidden rounded-xl border border-border bg-card transition-all hover:border-primary/40 hover:shadow-lg hover:-translate-y-1">
                                    <div className="aspect-[16/10] overflow-hidden bg-secondary relative">
                                        <img src={absUrl(p.thumbnail)} alt={p.title} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                                        <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-70" />
                                        <div className="absolute inset-0 flex items-center justify-center">
                                            <div className="h-14 w-14 rounded-full bg-white/90 backdrop-blur flex items-center justify-center shadow-lg group-hover:bg-primary group-hover:text-white transition-all">
                                                <Play className="h-5 w-5 ml-0.5" fill="currentColor" />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="p-6">
                                        {p.eyebrow && <div className="text-[10px] font-bold uppercase tracking-widest text-primary mb-1">{p.eyebrow}</div>}
                                        <div className="text-xs font-semibold text-primary tracking-wider uppercase">{p.category}</div>
                                        <h3 className="mt-2 font-heading font-bold text-foreground leading-tight">{p.title}</h3>
                                        <p className="mt-2 text-sm text-muted-foreground line-clamp-2">{p.hero_description || p.description}</p>
                                        <div className="mt-4 flex flex-wrap gap-1.5">
                                            {p.tech_stack?.slice(0, 4).map(t => (
                                                <span key={t} className="text-[10px] font-semibold uppercase tracking-wider px-2 py-1 rounded bg-secondary text-secondary-foreground">{t}</span>
                                            ))}
                                        </div>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    )}
                </div>
            </section>
        </div>
    );
}

// ── Product Detail Page ───────────────────────────────────────────────────────
export function ProductDetail() {
    const { id } = useParams();
    const [item, setItem] = useState(null);
    const [notFound, setNotFound] = useState(false);
    useSEO({ title: item ? `${item.title} — Product | RKS Systems` : "Product | RKS Systems", description: item?.hero_description || item?.description });

    useEffect(() => {
        api.get(`/products/${id}`).then(({ data }) => setItem(data)).catch(() => setNotFound(true));
    }, [id]);

    if (notFound) return <Navigate to="/products" replace />;
    if (!item) return <div className="container-app py-24 text-center text-muted-foreground">Loading...</div>;

    const hasVideo       = !!(item.video_url);
    const hasArchDiagram = !!(item.architecture_diagram);
    const hasFeatures    = item.features?.length > 0;
    const hasWorkflow    = item.workflow_steps?.length > 0;
    const hasAgents      = item.agents?.length > 0;
    const hasProblems    = item.business_problems?.length > 0;
    const hasOutcomeStrip = item.outcome_strip?.length > 0;
    const hasOutcomes    = item.outcomes?.length > 0;

    const headline = item.hero_headline || item.title;
    const eyebrow  = item.eyebrow || item.category;
    const heroDesc = item.hero_description || item.description;

    return (
        <div data-testid={`page-product-${item.id}`}>
            {/* ── Hero ── */}
            <section className="bg-[hsl(222_47%_7%)] text-white noise">
                <div className="container-app py-16 lg:py-24">
                    <Link to="/products" className="inline-flex items-center gap-1.5 text-sm text-white/60 hover:text-white mb-8">
                        <ArrowLeft className="h-3.5 w-3.5" /> All products
                    </Link>
                    <div className="grid lg:grid-cols-[1.6fr,1fr] gap-12 items-start">
                        <div>
                            <span className="overline text-primary">{eyebrow}</span>
                            <h1 className="mt-4 font-heading text-4xl sm:text-5xl font-extrabold tracking-tight leading-[1.05]">{headline}</h1>
                            {item.hero_headline && item.title !== item.hero_headline && (
                                <p className="mt-2 text-sm font-semibold text-primary/80 tracking-wide">{item.title}</p>
                            )}
                            <p className="mt-5 text-lg text-white/70 leading-relaxed max-w-2xl">{heroDesc}</p>
                            <div className="mt-8 flex flex-wrap gap-3">
                                <Link to="/schedule-meeting" data-testid="hero-cta-demo" className="inline-flex items-center gap-2 rounded-xl bg-primary px-6 py-3 text-sm font-semibold text-white hover:bg-primary/90 transition-colors">
                                    <CalendarDays className="h-4 w-4" />Schedule a Demo
                                </Link>
                                <Link to="/contact" data-testid="hero-cta-pilot" className="inline-flex items-center gap-2 rounded-xl border border-white/20 bg-white/10 px-6 py-3 text-sm font-semibold text-white hover:bg-white/20 transition-colors">
                                    <Rocket className="h-4 w-4" />Start a Free Pilot
                                </Link>
                            </div>
                            <div className="mt-8 flex flex-wrap gap-2">
                                {item.tech_stack?.map(t => (
                                    <span key={t} className="inline-flex items-center rounded-md border border-white/20 bg-white/[0.05] px-3 py-1.5 text-xs font-semibold text-white/80">{t}</span>
                                ))}
                            </div>
                        </div>
                        {/* Right: video / thumbnail / lifecycle visual */}
                        <div className="rounded-xl border border-white/10 overflow-hidden aspect-video bg-white/[0.03]">
                            {hasVideo ? (
                                item.video_url.includes("youtube") ? (
                                    <iframe title={item.title} src={item.video_url} className="w-full h-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
                                ) : (
                                    <video src={absUrl(item.video_url)} controls className="w-full h-full object-cover" data-testid="product-video" />
                                )
                            ) : item.thumbnail ? (
                                <img src={absUrl(item.thumbnail)} alt={item.title} className="w-full h-full object-cover opacity-90" />
                            ) : (
                                <div className="w-full h-full flex flex-col items-center justify-center gap-3 p-6">
                                    <div className="text-[10px] font-bold uppercase tracking-widest text-primary">Invoice Lifecycle</div>
                                    {["01 Collect", "02 Extract", "03 Validate", "04 Match", "05 Decide", "06 Analyze"].map(s => (
                                        <div key={s} className="text-sm font-semibold text-white/70">{s}</div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </section>

            {/* ── Outcome Strip ── */}
            {hasOutcomeStrip && <OutcomeStrip items={item.outcome_strip} />}

            {/* ── Product Overview ── */}
            {hasWorkflow && <ProductOverview item={item} />}
            {!hasWorkflow && item.description && (
                <section className="py-16 lg:py-20 border-b border-border" data-testid="section-solution">
                    <div className="container-app max-w-3xl">
                        <span className="overline text-primary">Overview</span>
                        <h2 className="mt-3 font-heading text-2xl sm:text-3xl font-bold">Solution Description</h2>
                        <p className="mt-4 text-foreground/80 leading-relaxed">{item.description}</p>
                    </div>
                </section>
            )}

            {/* ── How It Works ── */}
            {hasWorkflow && <WorkflowSection steps={item.workflow_steps} />}

            {/* ── Agent Architecture ── */}
            {hasAgents && <AgentArchitecture agents={item.agents} />}

            {/* ── Business Problems ── */}
            {hasProblems ? <BusinessProblemsSection problems={item.business_problems} /> : item.business_problem ? (
                <section className="py-16 lg:py-20 border-b border-border" data-testid="section-business-problem">
                    <div className="container-app max-w-3xl">
                        <span className="overline text-primary">The Challenge</span>
                        <h2 className="mt-3 font-heading text-2xl sm:text-3xl font-bold">Business Problem</h2>
                        <p className="mt-4 text-foreground/80 leading-relaxed">{item.business_problem}</p>
                    </div>
                </section>
            ) : null}

            {/* ── Key Capabilities ── */}
            {hasFeatures && (
                <section className="py-16 lg:py-20" data-testid="section-key-features">
                    <div className="container-app">
                        <span className="overline text-primary">Capabilities</span>
                        <h2 className="mt-3 font-heading text-2xl sm:text-3xl font-bold">Key Features</h2>
                        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
                            {item.features.map((feature, idx) => (
                                <div key={idx} data-testid={`product-feature-${idx}`} className="flex items-start gap-3 rounded-xl border border-border bg-white p-5">
                                    <div className="flex-shrink-0 h-8 w-8 rounded-md bg-primary/10 flex items-center justify-center">
                                        <CheckCircle2 className="h-4 w-4 text-primary" />
                                    </div>
                                    <p className="text-sm text-foreground/80 leading-relaxed">{feature}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* ── Business Outcomes ── */}
            {hasOutcomes && <BusinessOutcomes outcomes={item.outcomes} />}

            {/* ── Architecture Diagram ── */}
            {hasArchDiagram && (
                <section className="py-16 lg:py-20">
                    <div className="container-app">
                        <div className="flex items-center gap-3 mb-8">
                            <div className="h-8 w-8 rounded-md bg-primary/10 flex items-center justify-center">
                                <LayoutDashboard className="h-4 w-4 text-primary" />
                            </div>
                            <h2 className="font-heading text-2xl sm:text-3xl font-bold">Architecture Diagram</h2>
                        </div>
                        <div className="rounded-xl border border-border overflow-hidden bg-secondary/20 p-4">
                            <img src={absUrl(item.architecture_diagram)} alt={`${item.title} architecture diagram`} className="w-full rounded-lg object-contain max-h-[600px]" data-testid="product-architecture-diagram" />
                        </div>
                    </div>
                </section>
            )}

            {/* ── CTA ── */}
            <ProductCTA item={item} />
        </div>
    );
}
